import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocketChat } from '../contexts/WebSocketContext';
import { 
  Volume2, VolumeX, Minimize2, Maximize2, Search, Users, MapPin, 
  MessageCircle, ChevronDown, ChevronUp, Check
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { OnlineIndicator } from './OnlineIndicator';
import { ChatPopup } from './ChatPopup';
import { ProfileAvatar } from './ProfileAvatar';
import { MemberJourney } from './MemberJourney';

// Notification sound using Web Audio API
const createNotificationSound = () => {
  let audioContext = null;
  
  return () => {
    try {
      if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant two-tone chime
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1108.73, audioContext.currentTime + 0.1);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  };
};

export const ChatSidebar = ({ isOpen, onClose, onToggle }) => {
  const { user, api } = useAuth();
  const { soundEnabled, toggleSound, onAnyMessage, isConnected } = useWebSocketChat();
  const navigate = useNavigate();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [unreadConversations, setUnreadConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showUnread, setShowUnread] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Track previous unread count for sound notification
  const prevUnreadCountRef = useRef(0);
  const playSoundRef = useRef(null);
  const lastSoundPlayedRef = useRef(0);
  
  // Chat popups state - can have multiple open chats
  const [openChats, setOpenChats] = useState([]);
  // Format: [{ partner: {...}, profile: {...}, isMinimized: false }]

  // Initialize notification sound
  useEffect(() => {
    playSoundRef.current = createNotificationSound();
  }, []);

  // Play notification sound with rate limiting
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    
    const now = Date.now();
    if (now - lastSoundPlayedRef.current < 1000) return;
    
    if (playSoundRef.current) {
      playSoundRef.current();
      lastSoundPlayedRef.current = now;
    }
  }, [soundEnabled]);

  // Listen for WebSocket messages to refresh data in real-time
  useEffect(() => {
    if (!onAnyMessage || !user) return;
    
    const unsubscribe = onAnyMessage((data) => {
      // If we receive a new message that's for us, refresh the unread conversations
      if (data.type === 'new_message' && data.recipient_id === user.id) {
        // Immediate refresh to show the new unread message
        fetchData();
        playNotificationSound();
      }
    });
    
    return unsubscribe;
  }, [onAnyMessage, user, playNotificationSound]);

  useEffect(() => {
    if (user && isOpen) {
      fetchData();
      // Refresh every 30 seconds
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isOpen]);

  const fetchData = async () => {
    try {
      // Fetch online users
      const onlineRes = await api.get('/users/online');
      const onlineIds = onlineRes.data.online_user_ids || [];
      
      // Fetch all users with profiles to get online users details
      const usersRes = await api.get('/search');
      // Handle both array and object response format
      const allProfiles = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data.profiles || []);
      
      // Filter to only online users (excluding self)
      const online = allProfiles.filter(p => 
        onlineIds.includes(p.user_id) && p.user_id !== user.id
      );
      setOnlineUsers(online);
      
      // Fetch chat conversations with unread counts
      const chatConvRes = await api.get('/chat/conversations');
      const chatConvs = chatConvRes.data || [];
      setConversations(chatConvs);
      
      // Filter unread chat conversations
      const unread = chatConvs.filter(c => c.unread_count > 0);
      const currentUnreadCount = unread.reduce((sum, c) => sum + c.unread_count, 0);
      
      // Play sound if unread count increased (new message received)
      if (currentUnreadCount > prevUnreadCountRef.current && prevUnreadCountRef.current !== 0) {
        playNotificationSound();
      }
      prevUnreadCountRef.current = currentUnreadCount;
      
      setUnreadConversations(unread);
      
    } catch (error) {
      console.error('Error fetching chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOnlineUsers = onlineUsers.filter(u => 
    !searchQuery || u.pseudo?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Open a chat popup with a user
  const openChat = (userProfile) => {
    // Block chat with admins
    if (userProfile.is_admin) {
      console.warn('Cannot open chat with admin user');
      return;
    }
    
    // Check if chat is already open
    const existingIndex = openChats.findIndex(c => c.partner.id === userProfile.user_id);
    if (existingIndex >= 0) {
      // If minimized, restore it
      const updated = [...openChats];
      updated[existingIndex].isMinimized = false;
      setOpenChats(updated);
      return;
    }
    
    // Add new chat (max 3 open at once)
    const newChat = {
      partner: { id: userProfile.user_id, pseudo: userProfile.pseudo },
      profile: { 
        ...userProfile, 
        is_online: true,
        profile_picture: userProfile.profile_picture || userProfile.photos?.[0]?.url
      },
      isMinimized: false
    };
    
    setOpenChats(prev => {
      if (prev.length >= 3) {
        // Remove oldest chat
        return [...prev.slice(1), newChat];
      }
      return [...prev, newChat];
    });
  };

  // Open chat from conversation (unread discussion)
  const openChatFromConversation = (conv) => {
    // Block chat with admins
    if (conv.profile?.is_admin) {
      console.warn('Cannot open chat with admin user');
      return;
    }
    
    const existingIndex = openChats.findIndex(c => c.partner.id === conv.partner?.id);
    if (existingIndex >= 0) {
      const updated = [...openChats];
      updated[existingIndex].isMinimized = false;
      setOpenChats(updated);
      return;
    }
    
    const newChat = {
      partner: conv.partner,
      profile: { 
        ...conv.profile, 
        is_online: conv.is_online,
        profile_picture: conv.profile?.profile_picture
      },
      isMinimized: false
    };
    
    setOpenChats(prev => {
      if (prev.length >= 3) {
        return [...prev.slice(1), newChat];
      }
      return [...prev, newChat];
    });
  };

  const closeChat = (index) => {
    setOpenChats(prev => prev.filter((_, i) => i !== index));
  };

  const toggleMinimizeChat = (index) => {
    setOpenChats(prev => {
      const updated = [...prev];
      updated[index].isMinimized = !updated[index].isMinimized;
      return updated;
    });
  };

  if (!user || !isOpen) return null;

  if (isMinimized) {
    return (
      <div 
        className="fixed left-0 top-24 z-40 bg-dark-paper border border-white/10 rounded-r-lg shadow-xl cursor-pointer"
        onClick={() => setIsMinimized(false)}
      >
        <div className="p-3 flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
          <span className="text-white text-sm font-medium">{onlineUsers.length}</span>
          <Maximize2 className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="hidden md:flex fixed left-0 top-24 bottom-4 z-40 w-72 bg-dark-paper border-r border-white/10 shadow-xl flex-col" data-testid="chat-sidebar">
      {/* Member Journey - Profile Progress */}
      <MemberJourney />
      
      {/* Header */}
      <div className="p-3 border-b border-white/10 bg-dark flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
          <span className="text-white font-semibold text-sm whitespace-nowrap">Actuellement en ligne</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button 
            onClick={toggleSound}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title={soundEnabled ? "Désactiver les sons" : "Activer les sons"}
          >
            {soundEnabled ? (
              <Volume2 className="w-4 h-4 text-gold" />
            ) : (
              <VolumeX className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <button 
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Réduire"
          >
            <Minimize2 className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="p-2 border-b border-white/10 flex items-center gap-2 bg-dark/50">
        <button className="flex-1 flex items-center justify-between px-3 py-1.5 bg-dark-subtle rounded text-sm text-gray-300 hover:bg-white/10 transition-colors">
          <span className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-gold" />
            <span>Près de...</span>
          </span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        <button className="p-1.5 bg-dark-subtle rounded hover:bg-white/10 transition-colors">
          <Users className="w-4 h-4 text-gray-400" />
        </button>
        <button 
          onClick={() => setShowSearch(!showSearch)}
          className={`p-1.5 rounded transition-colors ${showSearch ? 'bg-gold/20 text-gold' : 'bg-dark-subtle hover:bg-white/10 text-gray-400'}`}
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Search Input */}
      {showSearch && (
        <div className="p-2 border-b border-white/10">
          <Input
            type="text"
            placeholder="Rechercher un membre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-sm bg-dark-subtle border-white/10"
          />
        </div>
      )}

      {/* Online Users List */}
      <div className="flex-1 overflow-y-auto scrollbar-visible">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        ) : filteredOnlineUsers.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {searchQuery ? 'Aucun résultat' : 'Aucun membre en ligne'}
          </div>
        ) : (
          <div className="py-1">
            {filteredOnlineUsers.map((profile) => (
              <button
                key={profile.user_id}
                onClick={() => openChat(profile)}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors group text-left"
                data-testid={`online-user-${profile.user_id}`}
              >
                {/* Avatar with colored border based on profile type */}
                <ProfileAvatar
                  profileType={profile.profile_type}
                  src={profile.profile_picture 
                    ? getImageUrl(profile.profile_picture)
                    : profile.photos?.[0]?.url 
                      ? getImageUrl(profile.photos[0].url)
                      : null
                  }
                  fallback={profile.pseudo?.[0]?.toUpperCase() || '?'}
                  size="md"
                  showOnline={true}
                  isOnline={true}
                />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-white text-sm font-medium truncate">
                      {profile.pseudo}
                    </span>
                    {profile.is_verified_by_team && (
                      <Check className="w-3.5 h-3.5 text-blue-400" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{profile.age} ans</span>
                    {profile.profile_type === 'couple' && (
                      <span className="px-1.5 py-0.5 bg-pink-500/20 text-pink-400 rounded text-[10px]">
                        Couple
                      </span>
                    )}
                  </div>
                </div>

                {/* Message icon on hover */}
                <MessageCircle className="w-4 h-4 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Unread Discussions Section */}
      {unreadConversations.length > 0 && (
        <>
          <button 
            onClick={() => setShowUnread(!showUnread)}
            className="w-full px-3 py-2 bg-red-500 text-white text-sm font-semibold flex items-center justify-between hover:bg-red-600 transition-colors animate-blink-message"
          >
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse-badge"></span>
              {unreadConversations.length} DISCUSSION{unreadConversations.length > 1 ? 'S' : ''} NON LUE{unreadConversations.length > 1 ? 'S' : ''}
            </span>
            {showUnread ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showUnread && (
            <div className="max-h-40 overflow-y-auto bg-dark/50">
              {unreadConversations.map((conv) => (
                <button
                  key={conv.partner?.id}
                  onClick={() => openChatFromConversation(conv)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition-colors text-left animate-blink-message"
                >
                  <OnlineIndicator isOnline={conv.is_online} size="sm" />
                  <span className="text-white text-sm flex-1 truncate font-semibold">{conv.partner?.pseudo}</span>
                  {conv.unread_count > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse-badge">
                      {conv.unread_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Footer - Quick access to messages */}
      <Link 
        to="/messages"
        className="p-3 border-t border-white/10 flex items-center justify-center gap-2 text-gold hover:bg-gold/10 transition-colors"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="text-sm font-medium">Voir toutes les conversations</span>
      </Link>

      {/* Chat Popups */}
      {openChats.map((chat, index) => (
        <ChatPopup
          key={chat.partner.id}
          partner={chat.partner}
          profile={chat.profile}
          isOpen={true}
          isMinimized={chat.isMinimized}
          onClose={() => closeChat(index)}
          onMinimize={() => toggleMinimizeChat(index)}
          position={index}
        />
      ))}
    </div>
  );
};

export default ChatSidebar;
